/**
 * Wrapper component for labs that use the project system.
 * This component will create a project manager for the current level and script
 * or channel, and will clean up the project manager on level change or unmount.
 */

import React, {useEffect} from 'react';

import header from '@cdo/apps/code-studio/header';
import {clearHeader} from '@cdo/apps/code-studio/headerRedux';
import useLifecycleNotifier from '@cdo/apps/lab2/hooks/useLifecycleNotifier';
import {setUpWithLevel} from '@cdo/apps/lab2/lab2Redux';
import Lab2Registry from '@cdo/apps/lab2/Lab2Registry';
import {resetProjectMetadata} from '@cdo/apps/lab2/redux/lab2ProjectRedux';
import {isReadOnlyWorkspace} from '@cdo/apps/lab2/redux/lab2ReduxSelectors';
import {LifecycleEvent} from '@cdo/apps/lab2/utils';
import {useAppDispatch, useAppSelector} from '@cdo/apps/util/reduxHooks';

import {useLevelProperties} from '../views/LevelPropertiesWrapper';

const ProjectContainer: React.FunctionComponent<ProjectContainerProps> = ({
  children,
  channelId,
}) => {
  const {levelProperties} = useLevelProperties();
  const currentLevelId = useAppSelector(state => state.progress.currentLevelId);
  const userId = useAppSelector(
    state => state.progress.viewAsUserId || undefined
  );
  const scriptId = useAppSelector(
    state => state.progress.scriptId || undefined
  );

  const isStandaloneProjectLevel = levelProperties.isProjectLevel;
  // Only show share and remix if hideShareAndRemix is explicitly false.
  const hideShareAndRemix = levelProperties.hideShareAndRemix !== false;
  const loadedChannelId = useAppSelector(
    state => state.lab.channel && state.lab.channel.id
  );
  const isOwnerOfChannel = useAppSelector(
    state => state.lab.channel && state.lab.channel.isOwner
  );

  const dispatch = useAppDispatch();
  const isReadOnly = useAppSelector(isReadOnlyWorkspace);

  // When the level changes, reset metadata relating to the project in redux.
  useLifecycleNotifier(LifecycleEvent.LevelLoadStarted, () =>
    dispatch(resetProjectMetadata())
  );

  useEffect(() => {
    // The redux types are very complicated, so in order to re-use this variable
    // we are defining it as any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let promise: any;
    // Before loading, clear the header so we don't accidentally show share and remix
    // for a level that does not allow it.
    dispatch(clearHeader());
    if (currentLevelId) {
      // If we have a level id, set up the lab with that level. If we also have a channel id,
      // we will load the project based on that channel id, otherwise we will look up a channel id
      // for the level.
      promise = dispatch(
        setUpWithLevel({
          levelId: parseInt(currentLevelId),
          userId,
          scriptId,
          levelProperties,
          channelId,
        })
      );
    }
    return () => {
      // If we have an early return, we will abort the promise in progress.
      // An early return could happen if the level is changed mid-load.
      promise?.abort();
    };
  }, [channelId, currentLevelId, scriptId, levelProperties, dispatch, userId]);

  useEffect(() => {
    window.addEventListener('beforeunload', event => {
      const projectManager = Lab2Registry.getInstance().getProjectManager();
      // Force a save before the page unloads, if there are unsaved changes.
      // If we need to force a save, prevent navigation so we can save first.
      // We skip this if we are already force reloading, as that will occur when
      // saving already encountered an issue. We also can skip this in read only mode,
      // as we never save in read only mode.
      if (
        projectManager &&
        !projectManager.isForceReloading() &&
        projectManager.hasUnsavedChanges() &&
        !isReadOnly
      ) {
        projectManager.cleanUp();
        event.preventDefault();
        event.returnValue = '';
      }
    });
  }, [isReadOnly]);

  useEffect(() => {
    // Ensure the header is cleared when we have a change,
    // then possibly load a new header if the level has one.
    dispatch(clearHeader());
    // If there is no channel, we can't load a header.
    if (loadedChannelId && isOwnerOfChannel) {
      if (isStandaloneProjectLevel) {
        // Standalone projects see project header (includes rename option).
        // Standalone projects always show share and remix.
        header.showProjectHeader();
      } else {
        // Project backed levels see project backed header, which can
        // conditionally show share and remix.
        header.showHeaderForProjectBacked({
          showShareAndRemix: !hideShareAndRemix,
        });
      }
    } else if (
      loadedChannelId &&
      !isOwnerOfChannel &&
      isStandaloneProjectLevel
    ) {
      // If we are viewing another user's project, and this is a standalone
      // project, show the minimal project header (project name and remix button).
      header.showMinimalProjectHeader();
    }
  }, [
    hideShareAndRemix,
    isStandaloneProjectLevel,
    loadedChannelId,
    isOwnerOfChannel,
    dispatch,
  ]);

  return <>{children}</>;
};

interface ProjectContainerProps {
  children: React.ReactNode;
  /** Channel ID for the project, if already known. Used for standalone projects and projects without levels. */
  channelId?: string;
}

export default ProjectContainer;
